// src/features/services/stock/components/LowStockAlerts.tsx
// ─────────────────────────────────────────────────────────────────────────────
// P3.1 / P3.2 — Low Stock Alerts panel.
// Shows SKUs below their reorder point, sorted by deficit.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import type { LowStockAlert } from '@/features/services/sales/api/stockApi';

interface LowStockAlertsProps {
  items: LowStockAlert[];
  isLoading: boolean;
}

export default function LowStockAlerts({ items, isLoading }: LowStockAlertsProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="pac-glass rounded-lg h-14 animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pac-glass-raised rounded-lg px-4 py-8 text-center text-gray-500">
        All stock levels are above their reorder points.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map(alert => {
        const pct = alert.reorder_point > 0
          ? Math.min(100, Math.round((alert.available / alert.reorder_point) * 100))
          : 0;
        const barColor = pct < 25 ? 'bg-red-500' : pct < 60 ? 'bg-amber-500' : 'bg-emerald-500';

        return (
          <div
            key={alert.sku}
            className="pac-glass-raised rounded-lg px-4 py-3 flex items-center gap-4"
          >
            {/* SKU label */}
            <div className="min-w-[120px]">
              <div className="font-medium text-white text-sm">{alert.sku.toUpperCase()}</div>
              <div className="text-[11px] text-gray-500">{alert.unit}</div>
            </div>

            {/* Progress bar */}
            <div className="flex-1">
              <div className="flex justify-between text-[11px] text-gray-400 mb-1">
                <span>Available: {alert.available}</span>
                <span>Reorder pt: {alert.reorder_point}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${barColor}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            {/* Deficit + suggestion */}
            <div className="text-right min-w-[100px]">
              <div className="text-red-400 font-semibold text-sm tabular-nums">
                –{alert.deficit}
              </div>
              <div className="text-[11px] text-gray-500">
                Order {alert.suggested_order}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
